import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, BookOpen, Play, Loader2, ArrowRight, ChevronDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { fetchBlogs, fetchVideos } from "@/lib/api";
import { searchWithFallback } from "@/lib/search";
import { sortByLatest, INITIAL_ITEMS } from "@/lib/paginate";
import { EXERCISE } from "@/constants/testIds";

const HERO =
  "https://images.pexels.com/photos/20035460/pexels-photo-20035460.jpeg?auto=compress&cs=tinysrgb&w=1400";

export default function Exercise() {
  const [blogs, setBlogs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [playing, setPlaying] = useState(null);
  const [expandedBlogs, setExpandedBlogs] = useState(false);
  const [expandedVideos, setExpandedVideos] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [b, v] = await Promise.all([fetchBlogs("exercise"), fetchVideos("exercise")]);
        setBlogs(b || []);
        setVideos(v || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const blogHay = (b) =>
    `${b.title} ${b.excerpt || ""} ${b.body} ${(b.tags || []).join(" ")}`;
  const videoHay = (v) =>
    `${v.title} ${v.description || ""} ${(v.tags || []).join(" ")}`;

  const blogsResult = useMemo(
    () => searchWithFallback(blogs, q, blogHay),
    [blogs, q]
  );
  const videosResult = useMemo(
    () => searchWithFallback(videos, q, videoHay),
    [videos, q]
  );
  const fBlogs = blogsResult.items;
  const fVideos = videosResult.items;
  const searching = q.trim().length > 0;
  const visibleBlogs = searching || expandedBlogs ? fBlogs : sortByLatest(fBlogs).slice(0, INITIAL_ITEMS);
  const visibleVideos = searching || expandedVideos ? fVideos : sortByLatest(fVideos).slice(0, INITIAL_ITEMS);
  const showFallbackNote =
    q.trim().length > 0 && (blogsResult.fallback || videosResult.fallback);

  return (
    <main data-testid={EXERCISE.page}>
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.2em] uppercase text-slate-500">Exercise</div>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl mt-3 text-slate-900 leading-[1.05]">
              Movement is medicine.
            </h1>
            <p className="text-slate-600 mt-6 text-lg max-w-xl leading-relaxed">
              Read short articles and watch short videos to build a routine that fits
              your body and your day — for diabetes, blood pressure and cholesterol.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-[2rem] overflow-hidden aspect-[4/3] border border-slate-200 shadow-lg">
              <img
                src={HERO}
                alt="Woman practising yoga"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-slate-500">Library</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-2 text-slate-900">
              Articles & videos
            </h2>
          </div>
          <div className="relative w-full md:w-96">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <Input
              data-testid={EXERCISE.search}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search articles & videos…"
              className="pl-11 h-12 rounded-full bg-white"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center text-slate-500">
            <Loader2 className="animate-spin mr-2" /> Loading library…
          </div>
        ) : (
          <>
            {showFallbackNote ? (
              <p
                data-testid="exercise-search-fallback"
                className="mb-4 text-sm text-slate-500"
              >
                No exact match for "{q}". Showing related articles and videos instead.
              </p>
            ) : null}
            <Tabs defaultValue="blogs">
            <TabsList>
              <TabsTrigger data-testid={EXERCISE.tabBlogs} value="blogs">
                Articles ({fBlogs.length})
              </TabsTrigger>
              <TabsTrigger data-testid={EXERCISE.tabVideos} value="videos">
                Videos ({fVideos.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="blogs" className="mt-8">
              {fBlogs.length === 0 ? (
                <p className="text-slate-500">No articles match your search.</p>
              ) : (
                <>
                <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                  {visibleBlogs.map((b) => (
                    <Link
                      key={b.id}
                      to={`/exercise/blog/${b.id}`}
                      data-testid={EXERCISE.blogCard(b.id)}
                      className="group"
                    >
                      <Card className="rounded-3xl border-slate-200 hover:shadow-xl hover:shadow-slate-200/60 transition-all duration-300 hover:-translate-y-1 h-full overflow-hidden">
                        {b.cover_image ? (
                          <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                            <img
                              src={b.cover_image}
                              alt={b.title}
                              loading="lazy"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        ) : null}
                        <CardContent className="p-7">
                          <Badge
                            variant="secondary"
                            className="bg-emerald-50 text-slate-700 border-emerald-100"
                          >
                            <BookOpen size={12} className="mr-1.5" /> Article
                          </Badge>
                          <div className="font-serif-display text-2xl text-slate-900 mt-4 leading-snug">
                            {b.title}
                          </div>
                          {b.excerpt ? (
                            <p className="text-slate-600 mt-3 text-sm leading-relaxed line-clamp-3">
                              {b.excerpt}
                            </p>
                          ) : null}
                          {b.tags && b.tags.length > 0 ? (
                            <div className="mt-4 flex flex-wrap gap-1.5">
                              {b.tags.map((t) => (
                                <span
                                  key={t}
                                  className="text-[10px] uppercase tracking-wider text-slate-500"
                                >
                                  #{t}
                                </span>
                              ))}
                            </div>
                          ) : null}
                          <div className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-slate-800 group-hover:gap-2 transition-all">
                            Read article <ArrowRight size={16} />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
                {!searching && !expandedBlogs && fBlogs.length > visibleBlogs.length ? (
                  <div className="mt-8 flex justify-center">
                    <Button
                      data-testid="exercise-blogs-show-more"
                      variant="outline"
                      onClick={() => setExpandedBlogs(true)}
                      className="rounded-full h-11 px-6"
                    >
                      Show all {fBlogs.length} articles
                      <ChevronDown size={16} className="ml-2" />
                    </Button>
                  </div>
                ) : null}
                </>
              )}
            </TabsContent>

            <TabsContent value="videos" className="mt-8">
              {fVideos.length === 0 ? (
                <p className="text-slate-500">No videos match your search.</p>
              ) : (
                <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {visibleVideos.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setPlaying(v)}
                      data-testid={EXERCISE.videoCard(v.id)}
                      className="group text-left"
                    >
                      <Card className="rounded-3xl border-slate-200 overflow-hidden hover:shadow-xl hover:shadow-slate-200/60 transition-all duration-300 hover:-translate-y-1 h-full">
                        <div className="aspect-video relative overflow-hidden bg-slate-100">
                          <img
                            src={`https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`}
                            alt={v.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 grid place-items-center bg-black/15 group-hover:bg-black/25 transition-colors">
                            <div className="w-14 h-14 rounded-full bg-white/90 grid place-items-center shadow-lg">
                              <Play size={22} className="text-slate-900 ml-1" />
                            </div>
                          </div>
                        </div>
                        <CardContent className="p-5">
                          <div className="font-serif-display text-xl text-slate-900 leading-snug">
                            {v.title}
                          </div>
                          {v.description ? (
                            <p className="text-sm text-slate-500 mt-2 line-clamp-2">
                              {v.description}
                            </p>
                          ) : null}
                        </CardContent>
                      </Card>
                    </button>
                  ))}
                </div>
                {!searching && !expandedVideos && fVideos.length > visibleVideos.length ? (
                  <div className="mt-8 flex justify-center">
                    <Button
                      data-testid="exercise-videos-show-more"
                      variant="outline"
                      onClick={() => setExpandedVideos(true)}
                      className="rounded-full h-11 px-6"
                    >
                      Show all {fVideos.length} videos
                      <ChevronDown size={16} className="ml-2" />
                    </Button>
                  </div>
                ) : null}
                </>
              )}
            </TabsContent>
          </Tabs>
          </>
        )}
      </section>

      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent data-testid={EXERCISE.videoDialog} className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {playing?.title}
            </DialogTitle>
            <DialogDescription className="sr-only">Video player</DialogDescription>
          </DialogHeader>
          {playing ? (
            <div className="aspect-video rounded-2xl overflow-hidden">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${playing.youtube_id}?autoplay=1`}
                title={playing.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </main>
  );
}
